import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthChrome } from '../components/auth/AuthChrome';
import { Banner, Button, Card, PasswordField, TextField } from '../components/primitives';
import { supabase } from '../lib/supabase';
import type { Session } from '@supabase/supabase-js';
import './SignIn.css';

const IDE_HANDOFF_PREFIX = 'penrose://auth/callback';
const FALLBACK_REVEAL_MS = 1500;

// Same-origin path: starts with a single `/`, not `//` or `/\`.
function isSafeNextPath(next: string | null): next is string {
  if (!next) return false;
  if (!next.startsWith('/')) return false;
  if (next.startsWith('//') || next.startsWith('/\\')) return false;
  return true;
}

function isSameOriginReferrer(): boolean {
  if (typeof document === 'undefined') return false;
  const ref = document.referrer;
  if (!ref) return false;
  try {
    return new URL(ref).origin === window.location.origin;
  } catch {
    return false;
  }
}

function buildDeepLink(redirectTo: string, session: Session): string {
  const sep = redirectTo.includes('?') ? '&' : '?';
  const params = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token
  });
  return `${redirectTo}${sep}${params.toString()}`;
}

function classifyAuthError(message: string | undefined): ReactNode {
  const msg = message?.toLowerCase() ?? '';
  if (
    msg.includes('invalid login credentials') ||
    msg.includes('invalid email or password') ||
    msg.includes('email or password')
  ) {
    return 'Email or password is incorrect.';
  }
  if (msg.includes('email not confirmed')) {
    return 'Please confirm your email before signing in.';
  }
  return "We couldn't sign you in. Try again.";
}

type View = 'form' | 'handing-off' | 'orphan';

type InviteData = { token: string; orgName: string; email: string };

export function SignIn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const rawRedirectTo = searchParams.get('redirect_to');
  const redirectTo =
    rawRedirectTo && rawRedirectTo.startsWith(IDE_HANDOFF_PREFIX) ? rawRedirectTo : null;
  const ideHandoff = redirectTo !== null;

  const inviteToken = searchParams.get('invite');

  const next = searchParams.get('next');
  const safeNext = isSafeNextPath(next) ? next : null;
  const sessionExpired = !!next;

  const prefilledEmail = (() => {
    const candidate = searchParams.get('email');
    if (!candidate) return '';
    return isSameOriginReferrer() ? candidate : '';
  })();

  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<ReactNode>(null);
  const [view, setView] = useState<View>('form');
  const [showRetry, setShowRetry] = useState(false);
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  // null = either no invite param, or invite token is invalid (silently
  // dropped per ux.md). When set, the banner renders + email is locked.
  const [invite, setInvite] = useState<InviteData | null>(null);

  const fallbackTimer = useRef<number | undefined>(undefined);
  const wentAway = useRef(false);

  const fireDeepLink = useCallback((link: string) => {
    setDeepLink(link);
    setView('handing-off');
    setShowRetry(false);
    wentAway.current = false;
    window.location.href = link;
    if (fallbackTimer.current !== undefined) window.clearTimeout(fallbackTimer.current);
    fallbackTimer.current = window.setTimeout(() => {
      if (document.visibilityState === 'visible') setShowRetry(true);
    }, FALLBACK_REVEAL_MS);
  }, []);

  // Session + invite resolution.
  //  - Already-signed-in + IDE handoff → fire deep link.
  //  - Already-signed-in + invite param → bounce to /invite (it auto-accepts
  //    or shows the mismatch card per ux.md).
  //  - Otherwise resolve the invite token (if any) so the invite banner can
  //    render with org name + locked email. Bad tokens are silently dropped.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: sessionData }, inviteResult] = await Promise.all([
        supabase.auth.getSession(),
        inviteToken
          ? supabase.rpc('resolve_invitation', { p_token: inviteToken })
          : Promise.resolve(null)
      ]);
      if (cancelled) return;

      const session = sessionData.session;

      if (session && inviteToken) {
        navigate(`/invite?token=${encodeURIComponent(inviteToken)}`, {
          replace: true
        });
        return;
      }

      if (session && ideHandoff && redirectTo) {
        // Falls through to setSessionChecked so the 'handing-off' view
        // renders while the OS hands off to the IDE.
        fireDeepLink(buildDeepLink(redirectTo, session));
      } else if (session) {
        // Signed-in, no invite, no IDE handoff — they shouldn't be on
        // /sign-in. Bounce to /dashboard.
        navigate('/dashboard', { replace: true });
        return;
      }

      if (inviteToken && inviteResult && !('error' in (inviteResult ?? {}) && inviteResult.error)) {
        const row = Array.isArray(inviteResult.data)
          ? inviteResult.data[0]
          : null;
        if (row && row.ok) {
          const invEmail = (row.email ?? '').toString();
          setInvite({
            token: inviteToken,
            orgName: (row.org_name ?? '').toString(),
            email: invEmail
          });
          setEmail(invEmail);
        }
      }

      setSessionChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [ideHandoff, redirectTo, fireDeepLink, inviteToken, navigate]);

  // Orphan-tab heuristic: after the deep link fires, if the user leaves the tab
  // (OS handed off to the IDE) and then returns, switch to the orphan view.
  // Proxy for the spec's "IDE consumed the single-use token" signal, which we
  // don't have wired end-to-end yet.
  useEffect(() => {
    if (view !== 'handing-off') return;
    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        wentAway.current = true;
      } else if (document.visibilityState === 'visible' && wentAway.current) {
        setView('orphan');
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [view]);

  useEffect(() => {
    return () => {
      if (fallbackTimer.current !== undefined) window.clearTimeout(fallbackTimer.current);
    };
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setErrorText(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setErrorText('Email and password are required.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password
      });
      if (error || !data.session) {
        setErrorText(classifyAuthError(error?.message));
        setSubmitting(false);
        return;
      }

      if (invite) {
        const { error: acceptError } = await supabase.rpc('accept_invitation', {
          p_token: invite.token
        });
        if (acceptError) {
          // Token consumed / expired / mismatch race. Send the user to the
          // invite landing — it will show the invalid card or mismatch.
          navigate(`/invite?token=${encodeURIComponent(invite.token)}`, {
            replace: true
          });
          return;
        }
        navigate('/dashboard', { replace: true });
        return;
      }

      if (ideHandoff && redirectTo) {
        fireDeepLink(buildDeepLink(redirectTo, data.session));
        return;
      }
      navigate(safeNext ?? '/dashboard', { replace: true });
    } catch {
      setErrorText(
        "We couldn't reach Penrose. Check your connection and try again."
      );
      setSubmitting(false);
    }
  }

  function handleRetry() {
    if (deepLink) {
      window.location.href = deepLink;
    }
  }

  function handleCloseTab() {
    window.close();
  }

  if (!sessionChecked) return null;

  const signUpHref = invite
    ? `/sign-up?invite=${encodeURIComponent(invite.token)}`
    : rawRedirectTo
      ? `/sign-up?redirect_to=${encodeURIComponent(rawRedirectTo)}`
      : '/sign-up';

  return (
    <>
      <AuthChrome pageLabel="Sign in" />
      <main className="si-main">
        {view === 'form' ? (
          <Card className="si-card" aria-labelledby="si-heading">
            <h1 id="si-heading" className="si-heading">
              Sign in
            </h1>

            {invite ? (
              <Banner
                kind="accent"
                title={`Sign in to accept your invitation to ${invite.orgName}`}
              >
                Sent to {invite.email}.
              </Banner>
            ) : null}

            {!invite && ideHandoff ? (
              <Banner kind="accent" title="Signing in to Penrose IDE">
                We'll send you back to your IDE when you're done.
              </Banner>
            ) : null}

            {!invite && sessionExpired ? (
              <Banner title="Your session expired">
                Please sign in again. We'll bring you back to where you were.
              </Banner>
            ) : null}

            {errorText ? <Banner kind="error">{errorText}</Banner> : null}

            <form className="si-form" onSubmit={handleSubmit} noValidate>
              <TextField
                id="si-email"
                label="Email"
                type="email"
                autoComplete="email"
                required
                autoFocus={!invite}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                readOnly={!!invite}
                data-locked={invite ? 'true' : undefined}
              />

              <PasswordField
                id="si-password"
                label="Password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                trailing={
                  <div className="si-forgot-row">
                    <Link to="/password-reset" className="si-forgot-link">
                      Forgot password?
                    </Link>
                  </div>
                }
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={submitting}
                className="si-submit"
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            <p className="si-alt">
              Need an account? <Link to={signUpHref}>Sign up</Link>
            </p>
          </Card>
        ) : null}

        {view === 'handing-off' ? (
          <Card className="si-card si-returning" aria-labelledby="si-returning-heading">
            <h1 id="si-returning-heading" className="si-heading">
              Returning you to Penrose IDE…
            </h1>
            <p className="si-returning-body">
              {showRetry
                ? "If your IDE didn't open, click below to retry. You can also close this tab and try Sign in again from the IDE."
                : 'Your IDE should open now.'}
            </p>
            {deepLink ? (
              <div className="si-deeplink" aria-label="Deep link">
                {deepLink}
              </div>
            ) : null}
            {showRetry ? (
              <Button variant="primary" size="md" onClick={handleRetry}>
                Retry deep link
              </Button>
            ) : null}
          </Card>
        ) : null}

        {view === 'orphan' ? (
          <Card className="si-card si-returning" aria-labelledby="si-orphan-heading">
            <h1 id="si-orphan-heading" className="si-heading">
              You're signed in.
            </h1>
            <p className="si-returning-body">You can close this tab.</p>
            <Button variant="primary" size="md" onClick={handleCloseTab}>
              Close tab
            </Button>
          </Card>
        ) : null}
      </main>
    </>
  );
}
